---
title: "Snagging — czym jest lista usterek i jak ją prowadzić na budowie"
description: "Snagging co to jest — anglojęzyczny termin na systematyczne wykrywanie i dokumentowanie usterek budowlanych. Jak prowadzić snagging list, by nic nie umknęło przed odbiorem."
date: "2026-06-07"
slug: "snagging-co-to-jest-lista-usterek"
faq:
  - q: "Czym różni się snagging od protokołu odbioru?"
    a: "Snagging to robocza, dynamiczna lista usterek prowadzona w trakcie i przed odbiorem. Protokół odbioru to formalny dokument prawny sporządzany w dniu przekazania obiektu. Celem snaggingu jest to, by do protokołu trafiało jak najmniej pozycji."
  - q: "Kto przeprowadza snagging?"
    a: "Snagging może przeprowadzać generalny wykonawca (wewnętrzna kontrola jakości), inspektor nadzoru inwestorskiego, dedykowany snagging inspektor lub nabywca z pomocą rzeczoznawcy. Nie ma ustawowego wymogu co do uprawnień dla wewnętrznych list usterek."
  - q: "Ile pozycji powinna zawierać snagging lista?"
    a: "Nie ma limitu — kompletna lista dla mieszkania może zawierać 20–100 pozycji, dla dużego obiektu biurowego lub osiedla — tysiące. Ważna jest systematyczność, nie eliminowanie pozycji dla uproszczenia."
  - q: "Jak długo należy archiwizować snagging listy?"
    a: "Zaleca się przechowywanie przez cały okres gwarancji i rękojmi (min. 5 lat od odbioru). Lista jest dowodem w sporach o jakość robót."
  - q: "Czy snagging można prowadzić papierowo?"
    a: "Można, ale papierowe listy są trudne w zarządzaniu przy dużej liczbie pozycji i wielu podwykonawcach. Narzędzia mobilne jak Pinveo umożliwiają prowadzenie listy bezpośrednio w terenie z automatyczną dokumentacją fotograficzną i statusami."
---

Snagging co to jest — pytanie to pojawia się coraz częściej w polskim budownictwie, szczególnie w firmach realizujących projekty z udziałem zagranicznych inwestorów lub generalnych wykonawców. Snagging to systematyczny proces wykrywania, dokumentowania i śledzenia usunięcia usterek budowlanych. W Polsce odpowiada mu lista usterek lub lista niezgodności prowadzona przed odbiorem końcowym.

---

## Czym jest snagging?

**Snagging** (ang. snagging list = lista usterek) to procedura stosowana w budownictwie brytyjskim i międzynarodowym polegająca na:

1. **Systematycznym przeglądzie** wykończonych (lub częściowo wykończonych) robót budowlanych.
2. **Dokumentowaniu każdej usterki** — lokalizacja, opis, zdjęcie, ocena pilności.
3. **Przypisaniu odpowiedzialności** do konkretnego podwykonawcy lub ekipy.
4. **Śledzeniu statusu** — każda usterka ma status: otwarta / w trakcie / zamknięta.
5. **Weryfikacji usunięcia** — inspektor potwierdza wykonanie naprawy, zamykając pozycję.

W polskiej praktyce budowlanej snagging odpowiada **końcowej kontroli jakości** przed przekazaniem obiektu lub lokalu. Stosują go generalny wykonawca, inwestor zastępczy, inspektor nadzoru inwestorskiego i zawodowi rzeczoznawcy podczas odbiorów.

---

## Snagging a protokół odbioru — różnica

| Aspekt | Snagging | Protokół odbioru |
|---|---|---|
| Kiedy | W trakcie budowy i przed odbiorem | Formalnie — w dniu przekazania |
| Cel | Wykrycie i usunięcie usterek przed odbiorem | Formalne potwierdzenie przekazania obiektu |
| Dokument | Robocza lista niezgodności | Prawnie wiążący protokół |
| Strony | Wewnętrzny (GW, inwestor, podwykonawcy) | Nabywca/zamawiający + wykonawca/deweloper |
| Ciągłość | Dynamiczny dokument aktualizowany na bieżąco | Statyczny snapshot z dnia odbioru |

Profesjonalne podejście zakłada przeprowadzenie snaggingu **przed** formalnym odbiorem — tak, by do protokołu trafiała lista usterek, których GW nie zdążył usunąć, a nie kompletna dokumentacja zaległości.

---

## Jak prowadzić listę snaggingową — krok po kroku

### Krok 1 — Przygotowanie planu budynku

Podstawą skutecznego snaggingu jest **plan kondygnacji lub rzut obiektu** z numerowanymi pomieszczeniami. Każda usterka będzie powiązana z konkretną lokalizacją na planie — unika to niejednoznaczności w opisach.

### Krok 2 — Zdefiniowanie kategorii usterek

Typowe kategorie stosowane w snagging lists:

- **Wykończenie** — malarstwo, tynki, płytki, podłogi.
- **Stolarka** — okna, drzwi, okucia.
- **Instalacje** — elektryka, wod-kan, ogrzewanie, wentylacja.
- **Elewacja i dach** — zewnętrzne elementy budynku.
- **BHP i bezpieczeństwo** — braki zabezpieczeń, niekompletne elementy.

### Krok 3 — Systematyczny obchód

Obchodź pomieszczenie po pomieszczeniu, obszar po obszarze — nigdy losowo. Dokumentuj każdą niezgodność z:

- **numerem pozycji** na liście,
- **lokalizacją** — numer pomieszczenia + element (np. „Salon 1A, ściana zachodnia"),
- **opisem usterki** (co, gdzie, dlaczego jest niezgodne ze standardem),
- **kategorią** i **oceną pilności**,
- **zdjęciem** z GPS i znacznikiem czasu,
- **odpowiedzialnym podwykonawcą** (jeśli znany).

### Krok 4 — Dystrybucja listy

Lista snaggingowa jest przekazywana odpowiednim podwykonawcom i kierownikowi budowy z terminem usunięcia usterek. Każdy podwykonawca odpowiada tylko za swoje pozycje.

### Krok 5 — Weryfikacja i zamykanie pozycji

Po zgłoszeniu usunięcia usterki inspektor weryfikuje w terenie i zamyka pozycję w liście. Fotografia „po naprawie" dołączona do listy dokumentuje zakończenie działań.

---

## Snagging w praktyce polskiego budownictwa

W Polsce procedury snaggingowe najczęściej stosują:

- **Inwestorzy instytucjonalni** (deweloperzy komercyjni, fundusze) — przy dużych obiektach biurowych i mieszkaniowych.
- **Generalny wykonawca** — wewnętrzna kontrola jakości przed przekazaniem inwestorowi.
- **Inspektorzy nadzoru inwestorskiego** — jako element nadzoru nad jakością robót.
- **Nabywcy mieszkań** — coraz częściej z pomocą zawodowych inspektorów budowlanych.

Art. 56 Prawa budowlanego nie wymaga wprost prowadzenia snagging list, ale dokumentacja niezgodności wspiera procedury odbiorowe i stanowi podstawę roszczeń z rękojmi lub gwarancji.

---

## Jak Pinveo wspiera snagging?

Pinveo zostało zaprojektowane z myślą o właśnie takich procesach — dynamicznych listach usterek z lokalizacją na planie:

- **Markery na rzucie PDF** — każda usterka to pinezka na planie kondygnacji z opisem, kategorią i zdjęciem.
- **Zdjęcia z GPS i znacznikiem czasu** — dowód miejsca i czasu stwierdzenia niezgodności.
- **Statusy usterek** — otwarta / w trakcie / zamknięta — śledzenie postępu usuwania.
- **Raport PDF i Excel** — gotowa lista snaggingowa z wszystkimi pozycjami, do przekazania podwykonawcom lub inwestorowi.
- **Praca offline** — inspektor dokumentuje bez zasięgu w nowo oddanych pomieszczeniach.
- **Weryfikacja po naprawie** — zdjęcie „po" zamknięte przy tej samej pozycji na liście.

Dowiedz się więcej: [Inwentaryzacja usterek z Pinveo](/zastosowania/inwentaryzacja-usterek).

Przeczytaj też: [Najczęstsze usterki przy odbiorze mieszkania — na co zwrócić uwagę](/blog/najczestsze-usterki-odbior-mieszkania).

---

## Podsumowanie

Snagging to systematyczny proces wykrywania, dokumentowania i zamykania usterek budowlanych przed odbiorem obiektu. Dobrze prowadzona snagging list minimalizuje liczbę usterek trafiających do formalnego protokołu i przyspiesza rozliczenie z podwykonawcami. Pinveo dostarcza mobilne narzędzie do prowadzenia list snaggingowych z lokalizacją na rzucie, historią statusów i gotowymi raportami.

---

## FAQ

### Czym różni się snagging od protokołu odbioru?

Snagging to robocza, dynamiczna lista usterek prowadzona w trakcie i przed odbiorem. Protokół odbioru to formalny dokument prawny sporządzany w dniu przekazania obiektu. Celem snaggingu jest to, by do protokołu trafiało jak najmniej pozycji.

### Kto przeprowadza snagging?

Snagging może przeprowadzać generalny wykonawca (wewnętrzna kontrola jakości), inspektor nadzoru inwestorskiego, dedykowany snagging inspektor lub nabywca z pomocą rzeczoznawcy. Nie ma ustawowego wymogu co do uprawnień dla wewnętrznych list usterek.

### Ile pozycji powinna zawierać snagging lista?

Nie ma limitu — kompletna lista dla mieszkania może zawierać 20–100 pozycji, dla dużego obiektu biurowego lub osiedla — tysiące. Ważna jest systematyczność, nie eliminowanie pozycji „dla uproszczenia".

### Jak długo należy archiwizować snagging listy?

Zaleca się przechowywanie przez cały okres gwarancji i rękojmi (min. 5 lat od odbioru). Lista jest dowodem w sporach o jakość robót.

### Czy snagging można prowadzić papierowo?

Można, ale papierowe listy są trudne w zarządzaniu przy dużej liczbie pozycji i wielu podwykonawcach. Narzędzia mobilne jak Pinveo umożliwiają prowadzenie listy bezpośrednio w terenie z automatyczną dokumentacją fotograficzną i statusami.
